import { useState, useCallback } from 'react';
import { SessionProvider, useSession } from '../components/integrated/SessionContext';
import MarketsTab from '../components/integrated/MarketsTab';
import DsvPortfolioTab from '../components/integrated/DsvPortfolioTab';
import RoyethTab from '../components/integrated/RoyethTab';
import LeverDsvTab from '../components/integrated/LeverDsvTab';
import OverviewTab from '../components/integrated/OverviewTab';
import YdmTab from '../components/integrated/YdmTab';
import COLORS from '../lib/colors';

const TABS = [
  { key: 'markets', label: 'Markets' },
  { key: 'ydm', label: 'YDM' },
  { key: 'dsv', label: 'DSV Portfolio' },
  { key: 'royeth', label: 'RoyETH' },
  { key: 'royethV2', label: 'RoyETH v2 vs sroywstETH' },
  { key: 'overview', label: 'Overview' },
];

function StatusDot({ status }) {
  const color = status === 'complete' ? COLORS.positive : COLORS.textTertiary;
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginLeft: 6 }} />;
}

function TabContent({ activeTab }) {
  switch (activeTab) {
    case 'markets': return <MarketsTab />;
    case 'dsv': return <DsvPortfolioTab />;
    case 'royeth': return <RoyethTab />;
    case 'royethV2': return <LeverDsvTab />;
    case 'overview': return <OverviewTab />;
    case 'ydm': return <YdmTab />;
    default: return null;
  }
}

function ExportImportControls() {
  const { exportState, importState } = useSession();
  const [importError, setImportError] = useState(null);

  const handleExport = useCallback(() => {
    const data = exportState();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `integrated-sim-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [exportState]);

  const handleImport = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const result = importState(ev.target.result);
        if (!result.success) setImportError(result.error);
        else setImportError(null);
      };
      reader.readAsText(file);
    };
    input.click();
  }, [importState]);

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      {importError && <span style={{ color: COLORS.negative, fontSize: 11 }}>{importError}</span>}
      <button onClick={handleImport} style={btnStyle}>Import</button>
      <button onClick={handleExport} style={btnStyle}>Export</button>
    </div>
  );
}

const btnStyle = {
  padding: '6px 14px', fontFamily: "'Fragment Mono', monospace", fontSize: 11,
  letterSpacing: '0.05em', textTransform: 'uppercase',
  background: 'transparent', color: COLORS.ink, border: `1px solid ${COLORS.borderLight}`,
  borderRadius: 2, cursor: 'pointer',
};

function SimulatorInner() {
  const [activeTab, setActiveTab] = useState('markets');
  const { state } = useSession();

  const tabStatus = (key) => {
    if (key === 'markets') {
      const names = Object.keys(state.markets);
      if (names.length === 0) return 'incomplete';
      return names.every(n => state.markets[n].status === 'complete') ? 'complete' : 'incomplete';
    }
    if (key === 'dsv') return state.dsv.status;
    if (key === 'royeth') return state.royeth.status;
    if (key === 'royethV2') return state.royethV2.status;
    return 'incomplete';
  };

  return (
    <div style={{ minHeight: '100vh', background: COLORS.bg, color: COLORS.ink }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: `1px solid ${COLORS.borderLight}` }}>
        <h1 style={{ fontFamily: "'Shippori Mincho B1', serif", fontSize: 20, fontWeight: 400, margin: 0 }}>
          Integrated Simulator
        </h1>
        <ExportImportControls />
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${COLORS.borderLight}`, padding: '0 24px' }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '12px 20px', fontFamily: "'Fragment Mono', monospace", fontSize: 12,
              letterSpacing: '0.05em', textTransform: 'uppercase', cursor: 'pointer',
              background: 'transparent', border: 'none',
              borderBottom: activeTab === tab.key ? `2px solid ${COLORS.accent}` : '2px solid transparent',
              color: activeTab === tab.key ? COLORS.ink : COLORS.textTertiary,
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            {tab.label}
            {tab.key !== 'overview' && tab.key !== 'ydm' && <StatusDot status={tabStatus(tab.key)} />}
          </button>
        ))}
      </div>

      <TabContent activeTab={activeTab} />
    </div>
  );
}

export default function IntegratedSimulator() {
  return (
    <SessionProvider>
      <SimulatorInner />
    </SessionProvider>
  );
}
